// src/state/ViewModeContext.js
//
// Provides a single `viewMode` value down the component tree, eliminating
// prop-drilling for permission checks.  Editable affordances (create, rename,
// delete, drag-reparent) are rendered only when `isEditable` is true.
//
// viewMode values:
//   'editor'        – authenticated owner / editor member
//   'viewer'        – authenticated viewer member (read-only)
//   'public-project'– unauthenticated public project share (read-only)
//   'public-doc'    – unauthenticated public doc share (read-only)
//
// Future roles (e.g. 'commentor') are added by extending the predicates below
// without touching any component.

import React, { createContext, useContext } from 'react';

export const ViewModeContext = createContext({
  viewMode: 'editor',
  isEditable: true,
  canComment: false,
  isPublic: false,
});

/**
 * Wrap a layout root in <ViewModeProvider viewMode="editor|viewer|...">
 * to make useViewMode() available to every child component.
 */
export function ViewModeProvider({ viewMode, children }) {
  const isEditable = viewMode === 'editor';
  // commentor role: can comment but not write docs (future)
  const canComment = viewMode === 'editor' || viewMode === 'commentor';
  const isPublic = viewMode === 'public-project' || viewMode === 'public-doc';

  return (
    <ViewModeContext.Provider value={{ viewMode, isEditable, canComment, isPublic }}>
      {children}
    </ViewModeContext.Provider>
  );
}

/**
 * useViewMode() – consume the view mode anywhere inside a layout root.
 * Returns { viewMode, isEditable, canComment, isPublic }.
 */
export function useViewMode() {
  return useContext(ViewModeContext);
}

export default ViewModeContext;
